import { MailIcon } from '@components/Icons/MailIcon';
import { useResendCooldown } from '@lib/hooks/auth/useResendCooldown';
import { api } from '@lib/network/api';
import { isEmailValid } from '@lib/utils/email';
import { Box, Button, Typography } from '@mui/material';
import { AuthButton } from '@pages/Auth/AuthButton';
import { AuthField } from '@pages/Auth/AuthField';
import { trackingStore } from '@stores/Tracking';
import { userStore } from '@stores/User';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import css from './VerifyEmail.module.scss';

export const VerifyEmail = observer(function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get('email');
  const displayEmail = emailParam || userStore.data?.email;
  const [emailInput, setEmailInput] = useState('');
  const [clicked, setClicked] = useState(false);
  // Seed from the store timestamp so a cooldown already in effect (e.g. right
  // after registering) shows on mount; later resends re-arm it via start().
  const cooldown = useResendCooldown({ initialStartedAt: userStore.lastResendTime });

  useEffect(() => {
    if (emailParam && userStore.data?.email && emailParam !== userStore.data.email) {
      userStore.logout({ redirect: false }).catch(() => {
        userStore.setToken(null);
      });
    }
  }, [emailParam, userStore.data?.email]);

  useEffect(() => {
    const emailMismatch =
      emailParam && userStore.data?.email && emailParam !== userStore.data.email;
    if (userStore.data?.isVerified && !emailMismatch) {
      navigate('/');
    }
  }, [userStore.data?.isVerified, emailParam, userStore.data?.email]);

  return (
    <div>
      <div className={css.confirmEmailBox}>
        <div className={css.confirmEmailIcon}>
          <MailIcon />
        </div>
        <div className={css.confirmEmailHeaderText}>
          <h2>Check your email</h2>
          <span>
            Please verify your email by clicking the link in the email we sent to{' '}
            <strong>{displayEmail ?? 'your email'}</strong>.
          </span>

          {!displayEmail && !userStore.token && (
            <>
              <span>
                Not sure we got the right one? Type in the email you used to register and try again.
              </span>
              <AuthField
                label="Email"
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                autoComplete="email"
                autoFocus
              />
            </>
          )}

          {cooldown.active ? (
            <span>
              Didn't receive the email in your inbox or spam folder? Please wait{' '}
              {cooldown.secondsLeft} seconds to resend it.
            </span>
          ) : (
            <AuthButton
              type="button"
              shineBehind
              disabled={clicked || (!displayEmail && !isEmailValid(emailInput))}
              onClick={async () => {
                if (!displayEmail) {
                  setClicked(true);
                  trackingStore.resendVerificationEmail();
                  await userStore.resendEmailVerification(emailInput).catch(console.error);
                  cooldown.start();
                  navigate(`/verify-email?email=${encodeURIComponent(emailInput)}`);
                  setClicked(false);
                  return;
                }
                setClicked(true);
                trackingStore.resendVerificationEmail();
                await userStore.resendEmailVerification(displayEmail).catch((error) => {
                  console.error(error);
                });
                cooldown.start();
                setClicked(false);
              }}
            >
              Resend verification email
            </AuthButton>
          )}
        </div>
      </div>
      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
        {displayEmail ? (
          <span className={css.emailVerificationContent}>
            <Typography variant="caption">Not {displayEmail}?</Typography>
            <Button
              variant="text"
              onClick={() => {
                const token = userStore.token;
                userStore.setToken(null);
                navigate('/login');
                if (token) {
                  api('/auth/logout', {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                  }).catch(() => {});
                }
              }}
              sx={{ color: 'text.primary' }}
            >
              <Typography variant="caption">
                <u>Logout.</u>
              </Typography>
            </Button>
          </span>
        ) : (
          <Button
            variant="text"
            onClick={() => {
              userStore.setToken(null);
              navigate('/login');
            }}
            sx={{ color: 'text.primary' }}
          >
            <Typography variant="caption">
              <u>Back to login.</u>
            </Typography>
          </Button>
        )}
      </Box>
    </div>
  );
});
